/**
 * dayPdfExport.js — branded single-day PDF report
 *
 * Generates a focused A4 landscape PDF for ONE delivery day:
 *   Page 1 · Cover with 6-tile KPI strip + auto-generated key takeaways
 *   Page 2 · Forecast vs Actual chart (same one shown on the page)
 *   Page 3+ · Full 96-block ledger, colour-coded by APE band
 *
 * Reuses the brand primitives, helpers and chart capture from
 * weeklyPdfExport.js so both PDFs feel like the same publication.
 */
import { jsPDF } from "jspdf";
import {
  LIME, INK, PAPER, TEXT, MUTED, FAINT, AMBER, RED, LINE,
  PAGE, COL_W,
  fmt, fmtSigned, safe, prettyDate, MONTHS,
  paintBg, drawHeader, drawFooter, drawKpi, drawLogo,
  renderChartToImg,
} from "./weeklyPdfExport";

/* ─────────── Stats over a single day's 96 blocks ─────────── */
function aggregateDay(rows) {
  let sumAbsErr = 0, sumActual = 0, sumErr = 0, sumSqErr = 0;
  let sumForecast = 0, inBand = 0;
  rows.forEach((r) => {
    sumAbsErr += Math.abs(r.err);
    sumActual += r.actual;
    sumForecast += r.p50;
    sumErr += r.err;
    sumSqErr += r.err * r.err;
    if (r.actual >= r.p10 && r.actual <= r.p90) inBand += 1;
  });
  const n = rows.length || 1;
  const wmape = sumActual ? (sumAbsErr / sumActual) * 100 : 0;
  const mape = rows.reduce((a, r) => a + (r.ape || 0), 0) / n;
  return {
    blocks: n,
    accuracy: 100 - wmape,
    wmape,
    mape,
    mae: sumAbsErr / n,
    rmse: Math.sqrt(sumSqErr / n),
    coverage: (inBand / n) * 100,
    bias: sumErr / n,
    meanForecast: sumForecast / n,
    meanActual: sumActual / n,
  };
}

function buildDayFindings(dayMeta, stats) {
  const isoDate = prettyDate(dayMeta.date);
  const out = [];
  out.push(
    `Delivery ${isoDate}: ${fmt(stats.accuracy, 1)}% volume-weighted accuracy across ${stats.blocks} fifteen-minute blocks. Forecast was published blind, before the IEX DAM auction closed.`
  );
  out.push(
    `Mean absolute block-level error Rs ${Math.round(stats.mae)}/MWh (RMSE Rs ${Math.round(stats.rmse)}/MWh). The model's day-mean forecast came in at Rs ${Math.round(stats.meanForecast)} vs the realised day-mean Rs ${Math.round(stats.meanActual)} — a ${fmtSigned(stats.bias)} Rs/MWh ${stats.bias >= 0 ? "over" : "under"}-forecast bias.`
  );
  out.push(
    `P10–P90 prediction interval captured ${fmt(stats.coverage, 0)}% of realised prints (target >=80%) — ${stats.coverage >= 80 ? "calibrated as designed" : "tighter than realised volatility, an honest miss"}.`
  );
  return out;
}

/* ─────────── Page 1: Cover ─────────── */
function drawDayCover(pdf, dayMeta, stats, findings, totalPages) {
  paintBg(pdf);

  // Brand watermark — top-right
  pdf.setGState(new pdf.GState({ opacity: 0.07 }));
  drawLogo(pdf, PAGE.w - 200, 30, 200);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  drawHeader(pdf, `DAILY REPORT    ${prettyDate(dayMeta.date).toUpperCase()}    BLIND DAY-AHEAD`);

  /* ── Title block ─────────────────────────────────────────────── */
  // Eyebrow above the title — tiny lime mono caps for context
  pdf.setFont("courier", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(LIME);
  pdf.text(
    `${dayMeta.dow.toUpperCase()} · BLIND DAY-AHEAD FORECAST · IEX DAM N-REGION`,
    PAGE.m, 88
  );

  // Huge day title
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(40);
  pdf.setTextColor(TEXT);
  pdf.text(`${dayMeta.label}, ${dayMeta.date.slice(0, 4)}`, PAGE.m, 130);

  // "PowerBESS forecast vs IEX DAM clearing price." — "vs" in lime italic, mirrors weekly cover
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(13);
  pdf.setTextColor(MUTED);
  pdf.text("PowerBESS forecast", PAGE.m, 154);
  let cx = PAGE.m + pdf.getTextWidth("PowerBESS forecast ");
  pdf.setFont("helvetica", "italic");
  pdf.setTextColor(LIME);
  pdf.text("vs", cx, 154);
  cx += pdf.getTextWidth("vs ");
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(MUTED);
  pdf.text(" IEX DAM clearing price.", cx, 154);

  // Engine + methodology stamp
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7.5);
  pdf.setTextColor(FAINT);
  pdf.text(
    "ENGINE GBM2  ·  96 BLOCKS  ·  15-MIN RESOLUTION  ·  PUBLISHED PRE-AUCTION  ·  NO HINDSIGHT",
    PAGE.m, 170
  );

  /* ── 6-tile KPI strip ────────────────────────────────────────── */
  const kpis = [
    { label: "Accuracy",        value: `${fmt(stats.accuracy, 1)}%`,    sub: "100 - WMAPE, vol-wt", hi: true },
    { label: "WMAPE",           value: `${fmt(stats.wmape, 2)}%`,       sub: "Sum|err| / Sum|act|" },
    { label: "MAPE",            value: `${fmt(stats.mape, 1)}%`,        sub: "block-mean" },
    { label: "Mean abs error",  value: `Rs ${Math.round(stats.mae)}`,   sub: "per MWh" },
    { label: "P10-P90 cover",   value: `${fmt(stats.coverage, 0)}%`,    sub: "target >=80%",
      valueColor: stats.coverage >= 80 ? LIME : AMBER },
    { label: "Bias",            value: `${fmtSigned(stats.bias)}`,      sub: stats.bias >= 0 ? "over-forecast (Rs/MWh)" : "under-forecast (Rs/MWh)",
      valueColor: Math.abs(stats.bias) > 400 ? AMBER : TEXT },
  ];
  const gap = 10;
  const kpiW = (COL_W - gap * (kpis.length - 1)) / kpis.length;
  const kpiH = 86;
  const kpiY = 196;
  kpis.forEach((k, i) => {
    drawKpi(pdf, PAGE.m + i * (kpiW + gap), kpiY, kpiW, kpiH, k);
  });

  /* ── Two-column middle: takeaways + day-at-a-glance ─────────── */
  const midY  = kpiY + kpiH + 26;        // 308
  const leftW = COL_W * 0.60;
  const rightX = PAGE.m + leftW + 24;
  const rightW = COL_W - leftW - 24;

  // Left: section heading
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12.5);
  pdf.setTextColor(TEXT);
  pdf.text("Key takeaways", PAGE.m, midY);
  // micro underline accent — feels like a publication
  pdf.setDrawColor(LIME);
  pdf.setLineWidth(1.5);
  pdf.line(PAGE.m, midY + 3, PAGE.m + 28, midY + 3);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(TEXT);
  let yy = midY + 24;
  findings.forEach((f) => {
    pdf.setFillColor(LIME);
    pdf.circle(PAGE.m + 4, yy - 4, 1.8, "F");
    const lines = pdf.splitTextToSize(safe(f), leftW - 16);
    pdf.setTextColor(TEXT);
    pdf.text(lines, PAGE.m + 14, yy);
    yy += lines.length * 12 + 10;
  });

  // Right: Day at a glance
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12.5);
  pdf.setTextColor(TEXT);
  pdf.text("Day at a glance", rightX, midY);
  pdf.setDrawColor(LIME);
  pdf.setLineWidth(1.5);
  pdf.line(rightX, midY + 3, rightX + 28, midY + 3);

  pdf.setFont("courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(FAINT);
  pdf.text("DAY-MEAN PRICE COMPARISON", rightX, midY + 18);

  // Two bars: Forecast vs Actual
  const barsY  = midY + 32;
  const barH   = 26;
  const barGap = 18;
  const maxV   = Math.max(stats.meanForecast, stats.meanActual) * 1.05 || 1;
  const drawBar = (label, value, color, barIdx) => {
    const yB = barsY + barIdx * (barH + barGap);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(MUTED);
    pdf.text(label, rightX, yB - 5);
    // track
    pdf.setFillColor(LINE);
    pdf.roundedRect(rightX, yB, rightW, barH, 3, 3, "F");
    // fill
    const fillW = (value / maxV) * rightW;
    pdf.setFillColor(color);
    pdf.roundedRect(rightX, yB, fillW, barH, 3, 3, "F");
    // value label
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    const txt = `Rs ${Math.round(value).toLocaleString("en-IN")}`;
    const txtW = pdf.getTextWidth(txt);
    if (fillW > txtW + 18) {
      pdf.setTextColor(INK);
      pdf.text(txt, rightX + fillW - 10, yB + barH / 2 + 3.5, { align: "right" });
    } else {
      pdf.setTextColor(TEXT);
      pdf.text(txt, rightX + fillW + 8, yB + barH / 2 + 3.5);
    }
  };
  drawBar("PowerBESS forecast (p50)", stats.meanForecast, LIME, 0);
  drawBar("IEX DAM realised", stats.meanActual, AMBER, 1);

  // Bias caption
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(FAINT);
  pdf.text(
    `BIAS  ${fmtSigned(stats.bias)} RS/MWH  ${stats.bias >= 0 ? "(OVER)" : "(UNDER)"}`,
    rightX, barsY + 2 * (barH + barGap) + 4
  );

  /* ── Methodology strip at the bottom — fills the dead space ── */
  const methYStart = 462;
  const methCellH = 56;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(TEXT);
  pdf.text("How this was computed", PAGE.m, methYStart - 14);
  pdf.setDrawColor(LIME);
  pdf.setLineWidth(1.5);
  pdf.line(PAGE.m, methYStart - 11, PAGE.m + 28, methYStart - 11);

  const methods = [
    { num: "01", title: "BLIND",         body: "Forecast published before the IEX DAM auction closed." },
    { num: "02", title: "REFIT-PER-DAY", body: "Fresh model fit every delivery — no overfit memory." },
    { num: "03", title: "TARGET-MASKED", body: "No information from the delivery day in any feature." },
    { num: "04", title: "NO SELECTION",  body: "All 10 days in the public report. No cherry-picking." },
  ];
  const mGap = 10;
  const mW = (COL_W - mGap * (methods.length - 1)) / methods.length;
  methods.forEach((m, i) => {
    const mx = PAGE.m + i * (mW + mGap);
    // card
    pdf.setFillColor(PAPER);
    pdf.setDrawColor(LINE);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(mx, methYStart, mW, methCellH, 5, 5, "FD");
    // number (faint, large)
    pdf.setFont("courier", "bold");
    pdf.setFontSize(7);
    pdf.setTextColor(LIME);
    pdf.text(m.num, mx + 10, methYStart + 14);
    // title
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(TEXT);
    pdf.text(m.title, mx + 10, methYStart + 28);
    // body
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(MUTED);
    const bodyLines = pdf.splitTextToSize(m.body, mW - 20);
    pdf.text(bodyLines, mx + 10, methYStart + 41);
  });

  drawFooter(pdf, 1, totalPages);
}

/* ─────────── Page 2: Chart ─────────── */
async function drawChartPage(pdf, dayMeta, rows, mountNode) {
  pdf.addPage();
  paintBg(pdf);
  drawHeader(pdf, `CHART    ${prettyDate(dayMeta.date).toUpperCase()}`);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.setTextColor(TEXT);
  pdf.text(`${dayMeta.label} — hourly forecast vs actual`, PAGE.m, 88);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(MUTED);
  pdf.text(
    "Lime line: PowerBESS p50 forecast.   Amber line: IEX DAM realised clearing price.   Lime band: P10-P90 forecast interval.",
    PAGE.m, 106
  );

  const img = await renderChartToImg(rows, mountNode);
  // chart area: full width, 380pt tall
  const chartX = PAGE.m;
  const chartY = 124;
  const chartW = COL_W;
  const chartH = 380;
  pdf.addImage(img, "JPEG", chartX, chartY, chartW, chartH, undefined, "FAST");

  // Caption strip under chart
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(FAINT);
  pdf.text(
    "BLIND WALK-FORWARD  ·  REFIT-PER-DAY  ·  NO HINDSIGHT  ·  GBM2 ENGINE  ·  IEX DAM N-REGION",
    PAGE.m, chartY + chartH + 14
  );
}

/* ─────────── Page 3+: Ledger ─────────── */
function drawLedgerPages(pdf, dayMeta, rows, startPage, totalPages) {
  // Layout: 2 columns of 24 rows per page · 48 blocks per page · 2 pages.
  const COLS = 2;
  const ROWS_PER_COL = 24;
  const BLOCKS_PER_PAGE = COLS * ROWS_PER_COL;
  const totalLedgerPages = Math.ceil(rows.length / BLOCKS_PER_PAGE);

  const headers = ["BLK", "TIME", "FORECAST", "P10", "P90", "ACTUAL", "ERR", "APE"];
  const widths  = [22, 38, 60, 46, 46, 56, 50, 38];   // pt per column, total ≈ 356pt
  const tableW  = widths.reduce((a, b) => a + b, 0);

  for (let p = 0; p < totalLedgerPages; p++) {
    pdf.addPage();
    paintBg(pdf);
    drawHeader(pdf, `LEDGER ${p + 1}/${totalLedgerPages}    ${prettyDate(dayMeta.date).toUpperCase()}    96 BLOCKS`);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(TEXT);
    pdf.text("Full 15-minute ledger", PAGE.m, 88);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(MUTED);
    pdf.text(
      "All prices in Rs/MWh.   Rows tinted by APE: green <=5%, amber <=15%, red >15%.",
      PAGE.m, 104
    );

    // 2 columns positioned at left and right halves of usable width
    const gapBetweenCols = COL_W - 2 * tableW > 0 ? (COL_W - 2 * tableW) : 18;
    const colStartXs = [
      PAGE.m,
      PAGE.m + tableW + gapBetweenCols,
    ];
    const tableYStart = 126;
    const rowH = 14;

    colStartXs.forEach((startX, ci) => {
      // header row
      let cx = startX;
      pdf.setFillColor(PAPER);
      pdf.rect(startX, tableYStart, tableW, 16, "F");
      pdf.setDrawColor(LIME);
      pdf.setLineWidth(0.5);
      pdf.line(startX, tableYStart + 16, startX + tableW, tableYStart + 16);

      pdf.setFont("courier", "bold");
      pdf.setFontSize(7);
      pdf.setTextColor(LIME);
      headers.forEach((h, i) => {
        const align = i >= 2 ? "right" : "left";
        const tx = align === "right" ? cx + widths[i] - 5 : cx + 5;
        pdf.text(h, tx, tableYStart + 11, { align });
        cx += widths[i];
      });

      // Body rows
      const blockOffset = p * BLOCKS_PER_PAGE + ci * ROWS_PER_COL;
      for (let i = 0; i < ROWS_PER_COL; i++) {
        const r = rows[blockOffset + i];
        if (!r) break;
        const y = tableYStart + 16 + i * rowH;

        // Color by APE band (lightly)
        const ape = r.ape || 0;
        let tint = null;
        if (ape <= 5) tint = [0.55, 0.95, 0.0, 0.07];  // lime
        else if (ape <= 15) tint = [1, 0.72, 0, 0.08];  // amber
        else tint = [1, 0.48, 0.48, 0.10];              // red
        const [rR, rG, rB, aA] = tint;
        pdf.setGState(new pdf.GState({ opacity: aA }));
        pdf.setFillColor(Math.round(rR * 255), Math.round(rG * 255), Math.round(rB * 255));
        pdf.rect(startX, y, tableW, rowH, "F");
        pdf.setGState(new pdf.GState({ opacity: 1 }));

        // Row hairline
        pdf.setDrawColor(LINE);
        pdf.setLineWidth(0.25);
        pdf.line(startX, y + rowH, startX + tableW, y + rowH);

        // Cell text
        cx = startX;
        const cells = [
          { v: String(r.b), align: "left" },
          { v: r.t,         align: "left" },
          { v: r.p50.toLocaleString("en-IN"),    align: "right" },
          { v: r.p10.toLocaleString("en-IN"),    align: "right" },
          { v: r.p90.toLocaleString("en-IN"),    align: "right" },
          { v: r.actual.toLocaleString("en-IN"), align: "right" },
          { v: fmtSigned(r.err),                 align: "right",
            color: r.err >= 0 ? TEXT : RED },
          { v: `${fmt(ape, 1)}%`,                align: "right",
            color: ape <= 5 ? LIME : (ape <= 15 ? AMBER : RED) },
        ];

        cells.forEach((c, ci2) => {
          pdf.setFont("courier", ci2 === 0 ? "bold" : "normal");
          pdf.setFontSize(7);
          pdf.setTextColor(c.color || TEXT);
          const tx = c.align === "right" ? cx + widths[ci2] - 5 : cx + 5;
          pdf.text(safe(c.v), tx, y + 9.5, { align: c.align });
          cx += widths[ci2];
        });
      }
    });

    drawFooter(pdf, startPage + p, totalPages);
  }
  return totalLedgerPages;
}

/* ─────────── Entry point ─────────── */
export async function exportDayPDF({ forecastData, day, mountNode, onProgress }) {
  const rows = (forecastData[day.date] || []).map((r) => ({ ...r }));
  if (!rows.length) throw new Error("No data for selected day");

  const stats = aggregateDay(rows);
  const findings = buildDayFindings(day, stats);

  // 1 cover + 1 chart + N ledger pages (2 for 96 blocks at 48/page)
  const ledgerPages = Math.ceil(rows.length / 48);
  const totalPages = 2 + ledgerPages;

  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  onProgress?.(8, "Drawing cover…");
  drawDayCover(pdf, day, stats, findings, totalPages);

  onProgress?.(35, "Rendering chart…");
  await drawChartPage(pdf, day, rows, mountNode);

  onProgress?.(75, "Drawing ledger…");
  drawLedgerPages(pdf, day, rows, 3, totalPages);

  onProgress?.(100, "Done");

  const [y, m, d] = day.date.split("-").map(Number);
  const fileName = `PowerBESS-DAM-${d}${MONTHS[m - 1]}${y}-forecast-vs-actual.pdf`;
  return { pdf, fileName };
}
